'use client';

/**
 * Client-side WhatsApp link funnel tracking. When a customer arrives via a tracked welcome link
 * (`?ref=<token>`), we persist the token per store so a later "add to cart" — possibly on another
 * page — can still be attributed. Carts sent from the "Ao Vivo" board carry the same token.
 *
 * Server resolution + status advancement lives in POST /api/track/link.
 */

const REF_STORAGE_KEY = 'vupt-link-tracking-v1';
const ADDED_GUARD_PREFIX = 'vupt-link-added:';

const isBrowser = typeof window !== 'undefined';

function readTokens(): Record<string, string> {
  if (!isBrowser) return {};
  try {
    const raw = window.localStorage.getItem(REF_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, string>;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writeToken(storeId: string, token: string): void {
  if (!isBrowser) return;
  try {
    const tokens = readTokens();
    tokens[storeId] = token;
    window.localStorage.setItem(REF_STORAGE_KEY, JSON.stringify(tokens));
  } catch {
    /* storage unavailable — tracking is best-effort */
  }
}

function postEvent(storeId: string, event: 'opened' | 'added_to_cart', token: string): void {
  void fetch('/api/track/link', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ storeId, event, token }),
    keepalive: true,
  }).catch(() => {
    /* tracking must never break the journey */
  });
}

/** Persist the welcome-link token for a store and record the "opened" event. */
export function trackLinkOpened(storeId: string, token: string): void {
  if (!storeId || !token) return;
  writeToken(storeId, token);
  postEvent(storeId, 'opened', token);
}

/** Record "added to cart" once per store, only if the customer arrived via a welcome link. */
export function trackAddedToCartIfReferred(storeId: string): void {
  if (!isBrowser || !storeId) return;
  const token = readTokens()[storeId];
  if (!token) return;

  const guardKey = `${ADDED_GUARD_PREFIX}${storeId}`;
  try {
    if (window.sessionStorage.getItem(guardKey)) return;
    window.sessionStorage.setItem(guardKey, '1');
  } catch {
    /* no sessionStorage — fall through and still fire (idempotent server-side) */
  }
  postEvent(storeId, 'added_to_cart', token);
}

/**
 * A cart the store built and sent (Ao Vivo board) carries the conversation token. Opening it with
 * items already puts the customer in the cart, so it records a single `added_to_cart` (the server
 * fills the opened timestamp) and does not persist the token: later browsing on this device — maybe
 * by the staff member who opened their own link — is not attributed to that customer. An empty cart
 * is just an open, tracked like a welcome link.
 */
export function trackSharedCartLinkOpened(storeId: string, token: string, hasItems: boolean): void {
  if (!storeId || !token) return;
  if (hasItems) {
    postEvent(storeId, 'added_to_cart', token);
    return;
  }
  trackLinkOpened(storeId, token);
}
